import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import {
  type AdditionalOutflowsModel,
  type CreateAdditionalOutflowsModel,
  type UpdateAdditionalOutflowsModel,
} from 'src/app/models/additional-outflows.model';
import { GraphService } from './graph.service';
import { GRAPHQL_INPUTS } from './inputs';

export interface PaginatedAdditionalOutflowsModel {
  additionalOutflows: AdditionalOutflowsModel[];
  lastEvaluatedKeyId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AdditionalOutflowsService {
  constructor(private readonly graphService: GraphService) {}

  mutationCreateAdditionalOutflow(
    newAdditionalOutflow: CreateAdditionalOutflowsModel,
  ): Observable<{
    createAdditionalOutflow: AdditionalOutflowsModel;
  }> {
    return this.graphService.mutationAybApi<{
      createAdditionalOutflow: AdditionalOutflowsModel;
    }>({
      mutation: `mutation CreateAdditionalOutflow($additionalOutflow: ${GRAPHQL_INPUTS.InputCreateAdditionalOutflow}!){
        createAdditionalOutflow(additionalOutflow: $additionalOutflow){
          id,
          username,
          observation,
          outflowDate,
          outflowType,
          updatedAt,
          createdAt
        }
      }`,
      variables: { additionalOutflow: newAdditionalOutflow },
    });
  }

  getAll(lastEvaluatedKeyId?: string | null): Observable<{
    getAllAdditionalOutflows: PaginatedAdditionalOutflowsModel;
  }> {
    if (lastEvaluatedKeyId === undefined) {
      return from(
        (async () => {
          const allAdditionalOutflows: AdditionalOutflowsModel[] = [];
          let currentLastEvaluatedKeyId: string | null = null;

          do {
            const response: {
              getAllAdditionalOutflows: PaginatedAdditionalOutflowsModel;
            } = await firstValueFrom(
              this.getAllPage(currentLastEvaluatedKeyId),
            );
            const payload: PaginatedAdditionalOutflowsModel | undefined =
              response?.getAllAdditionalOutflows;
            const chunk: AdditionalOutflowsModel[] =
              payload?.additionalOutflows || [];

            if (chunk.length > 0) {
              allAdditionalOutflows.push(...chunk);
            }

            currentLastEvaluatedKeyId = payload?.lastEvaluatedKeyId || null;
          } while (Boolean(currentLastEvaluatedKeyId));

          return {
            getAllAdditionalOutflows: {
              additionalOutflows: allAdditionalOutflows,
              lastEvaluatedKeyId: null,
            },
          };
        })(),
      );
    }

    return this.getAllPage(lastEvaluatedKeyId);
  }

  getAllPage(lastEvaluatedKeyId?: string | null): Observable<{
    getAllAdditionalOutflows: PaginatedAdditionalOutflowsModel;
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllAdditionalOutflows: PaginatedAdditionalOutflowsModel;
    }>({
      query: `query GetAllAdditionalOutflows($lastEvaluatedKeyId: String) {
          getAllAdditionalOutflows(lastEvaluatedKeyId: $lastEvaluatedKeyId) {
            additionalOutflows {
              id,
              outflowDate,
              username,
              observation,
              outflowType,
              updatedAt,
              createdAt
            }
            lastEvaluatedKeyId
          }
        }`,
      variables: { lastEvaluatedKeyId },
    });
  }

  update(
    additionalOutflowId: string,
    updatedAdditionalOutflow: UpdateAdditionalOutflowsModel,
  ): Observable<{
    updateAdditionalOutflow: AdditionalOutflowsModel;
  }> {
    return this.graphService.mutationAybApi({
      mutation: `mutation UpdateAdditionalOutflow($additionalOutflowId: String!, $updatedAdditionalOutflow: ${GRAPHQL_INPUTS.InputUpdateAdditionalOutflow}!) {
        updateAdditionalOutflow(additionalOutflowId: $additionalOutflowId, updatedAdditionalOutflow: $updatedAdditionalOutflow) {
          id,
          outflowDate,
          username,
          observation,
          outflowType,
          outflowDate,
          updatedAt,
          createdAt
        }
      }`,
      variables: {
        additionalOutflowId,
        updatedAdditionalOutflow,
      },
    });
  }

  delete({ additionalOutflowId }: { additionalOutflowId: string }): Observable<{
    deleteAdditionalOutflow: boolean;
  }> {
    return this.graphService.mutationAybApi({
      mutation: `mutation DeleteAdditionalOutflow($additionalOutflowId: String!) {
        deleteAdditionalOutflow(additionalOutflowId: $additionalOutflowId)
      }`,
      variables: { additionalOutflowId },
    });
  }
}
